import type { TreatmentOverview } from "./hooks/useTreatmentOverviews";

export type RxTreatmentOverview = Extract<
  TreatmentOverview,
  { __typename: "RxTreatmentOverview" }
>;
export type OtcTreatmentOverview = Extract<
  TreatmentOverview,
  { __typename: "OTCTreatmentOverview" }
>;
export type LabTreatmentOverview = Extract<
  TreatmentOverview,
  { __typename: "LabTreatmentOverview" }
>;

export const isRxTreatmentOverview = (
  treatment: TreatmentOverview
): treatment is RxTreatmentOverview =>
  treatment.__typename === "RxTreatmentOverview";

export const isOtcTreatmentOverview = (
  treatment: TreatmentOverview
): treatment is OtcTreatmentOverview =>
  treatment.__typename === "OTCTreatmentOverview";

export const isLabTreatmentOverview = (
  treatment: TreatmentOverview
): treatment is LabTreatmentOverview =>
  treatment.__typename === "LabTreatmentOverview";

const BIOMARKER_INSTRUCTIONS_LINK_KEY = "BIOMARKERS_TEST_KIT_INSTRUCTIONS";

/**
 * The kit-instructions action link, when the catalog seeded one.
 *
 * Single source of the link-key match: supplement detail routing is gated on the
 * *absence* of this link, so a second copy of the lookup drifting out of sync
 * would route a biomarker kit into the supplement branch.
 */
export const findBiomarkerInstructionsLink = (treatment: TreatmentOverview) =>
  treatment.actionLinks?.find(
    (link) => link.linkKey === BIOMARKER_INSTRUCTIONS_LINK_KEY
  );

/** OTC biomarker kit — identified by the kit-instructions action link. */
export const isBiomarkerOtcTreatment = (
  treatment: TreatmentOverview
): boolean =>
  isOtcTreatmentOverview(treatment) &&
  !!findBiomarkerInstructionsLink(treatment);

/**
 * Any OTC supplement: OTC that is not a biomarker kit. Product-agnostic, so a
 * newly launched supplement (Vitality, Sleep, Skin) is covered without a change
 * here. Gates supplement detail-page routing and supplement status copy.
 *
 * Assumes OTC today is only biomarker kits vs supplements. A future third OTC
 * kind must not rely on "absence of the kit-instructions link" alone.
 */
export const isSupplementOtcTreatment = (
  treatment: TreatmentOverview
): boolean =>
  isOtcTreatmentOverview(treatment) && !isBiomarkerOtcTreatment(treatment);
